import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Text,
  VStack,
  useColorModeValue,
  IconButton,
  Link,
} from '@chakra-ui/react';
import {
  FaCcAmex,
  FaCcDiscover,
  FaCcMastercard,
  FaCcVisa,
  FaCreditCard,
  FaRegCreditCard,
  FaTrashCan,
} from 'react-icons/fa6';
import StripeLinkLogo from '../../../common/StripeLinkLogo';

const PaymentMethodsList = ({
  paymentMethods = [],
  isLoading = false,
  onAddPaymentMethod,
  onSetDefault,
  onRemove,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = '#D9D9D9';
  const errorBorderColor = useColorModeValue('red.200', 'red.700');

  const getCardIcon = (brand) => {
    const brands = {
      visa: FaCcVisa,
      mastercard: FaCcMastercard,
      amex: FaCcAmex,
      discover: FaCcDiscover,
    };

    return brands[brand?.toLowerCase()] || FaCreditCard;
  };

  const getCardDetails = (method) => {
    if (method.brand && method.last4) {
      return {
        brand: method.brand || 'Card',
        last4: method.last4 || '****',
        exp_month: method.exp_month,
        exp_year: method.exp_year,
      };
    }

    if (method.card) {
      return {
        brand: method.card.brand || 'Card',
        last4: method.card.last4 || '****',
        exp_month: method.card.exp_month,
        exp_year: method.card.exp_year,
      };
    }

    if (method.details && method.details.card) {
      return {
        brand: method.details.card.brand || 'Card',
        last4: method.details.card.last4 || '****',
        exp_month: method.details.card.exp_month,
        exp_year: method.details.card.exp_year,
      };
    }

    return {
      brand: 'Card',
      last4: '****',
      exp_month: null,
      exp_year: null,
    };
  };

  const formatExpiry = (month, year) => {
    if (!month || !year) return 'Unknown';
    return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
  };

  const renderCardMethod = (method) => {
    const cardDetails = getCardDetails(method);

    const isExpired =
      cardDetails.exp_month &&
      cardDetails.exp_year &&
      new Date(cardDetails.exp_year, cardDetails.exp_month) < new Date();

    const hasError = isExpired;

    const content = (
      <HStack spacing={4} align="flex-start">
        <Box color={method.is_default ? 'blue.500' : 'gray.500'}>
          <Icon as={getCardIcon(cardDetails.brand)} boxSize="28px" />
        </Box>

        <Box>
          <Flex alignItems="center" mb={1}>
            <Text fontWeight="medium" mr={2}>
              {cardDetails.brand} •••• {cardDetails.last4}
            </Text>
          </Flex>
          <Text fontSize="sm" color={hasError ? 'red.500' : 'gray.500'}>
            {cardDetails.exp_month && cardDetails.exp_year ? (
              <>
                Expires{' '}
                {formatExpiry(cardDetails.exp_month, cardDetails.exp_year)}
                {hasError && ' - Expired'}
              </>
            ) : (
              'No expiration date available'
            )}
          </Text>
        </Box>
      </HStack>
    );

    return { content, hasError };
  };

  const renderLinkMethod = (method) => {
    const content = (
      <HStack spacing={4} align="flex-start">
        <Box>
          <Flex alignItems="center" mb={1} height="28px">
            <StripeLinkLogo height="20px" />
          </Flex>
          {method.email && (
            <Text fontSize="sm" color="gray.500">
              {method.email}
            </Text>
          )}
        </Box>
      </HStack>
    );

    return { content, hasError: false };
  };

  const renderIdentityRow = (method) => {
    switch (method.type) {
      case 'link':
        return renderLinkMethod(method);
      case 'card':
      default:
        return renderCardMethod(method);
    }
  };

  const renderPaymentMethod = (method) => {
    const { content, hasError } = renderIdentityRow(method);

    return (
      <Box
        key={method.id}
        px={6}
        py={4}
        borderWidth="1.5px"
        borderRadius="15px"
        borderColor={
          hasError
            ? errorBorderColor
            : method.is_default
              ? 'orangePrimary'
              : borderColor
        }
        bg={method.is_default ? 'gradientOrange' : '#F8F8F8'}
        shadow="sm"
        position="relative"
        transition="all 0.2s"
        _hover={{
          shadow: 'md',
        }}
        width="100%"
      >
        <Flex justifyContent="space-between" alignItems="flex-start">
          {content}
          <Flex direction="column" gap={2} alignItems="flex-end">
            {method.is_default && (
              <Text
                textStyle="bodyFamilyRegular"
                fontSize="14px"
                color="orangePrimary"
              >
                Default
              </Text>
            )}
            <Flex direction="row" alignItems="center">
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Remove payment method"
                color="#989898"
                icon={<FaTrashCan />}
                m={0}
                p={0}
                boxSize="20px"
                minW={'20px'}
                _hover={{
                  bg: 'transparent',
                  color: 'orangePrimary',
                }}
                onClick={() => {
                  onRemove(method.id);
                }}
              />
            </Flex>
            {!method.is_default && (
              <Link
                textDecoration="none"
                color="#989898"
                aria-label="Set as default"
                onClick={() => {
                  onSetDefault(method.id);
                }}
                _hover={{
                  color: 'orangePrimary',
                  textDecoration: 'underline',
                }}
              >
                <Text textStyle="bodyFamilyRegular" fontSize="14px">
                  Make default
                </Text>
              </Link>
            )}
          </Flex>
        </Flex>
      </Box>
    );
  };

  const renderLoadingState = () => (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="md"
      borderColor={borderColor}
      bg={bgColor}
      shadow="sm"
      width="100%"
    >
      <HStack spacing={6} align="flex-start">
        <Skeleton boxSize="28px" />
        <Box flex="1">
          <Skeleton height="20px" width="70%" mb={2} />
          <Skeleton height="16px" width="50%" />
        </Box>
      </HStack>
    </Box>
  );

  return (
    <Box width="100%" mt="25px">
      <VStack spacing={6} align="stretch" width="100%">
        {isLoading ? (
          <>
            {renderLoadingState()}
            {renderLoadingState()}
          </>
        ) : paymentMethods.length > 0 ? (
          [...paymentMethods]
            .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
            .map(renderPaymentMethod)
        ) : (
          <Box
            py={8}
            borderWidth="1px"
            borderRadius="15px"
            borderColor={borderColor}
            bg={bgColor}
            textAlign="center"
          >
            <Icon as={FaRegCreditCard} boxSize="24px" color="gray.400" mb={2} />
            <Text>No payment methods available</Text>
          </Box>
        )}
        <Button
          size="md"
          variant="outline"
          onClick={onAddPaymentMethod}
          isDisabled={isLoading}
        >
          Add payment method
        </Button>
      </VStack>
    </Box>
  );
};

export default PaymentMethodsList;
